import * as moment from 'moment';

export interface PatientLists {
  recentPatients: any[];
  inpatients: any[];
  outpatients: any[];
  invites: any[];
  archives: any[];
  deceased: any[];
  alerts: any[];
}

/**
 * Formats a patient's date fields as they arrive from the server. It mutates the row and must run
 * once per row: run again, an admission date already in DD/MM/YYYY is misread as YYYY-MM-DD.
 */
export function formatPatientDates(patient: any): any {
  if (patient.dob) {
    const momentDob = moment.utc(patient.dob, 'DD/MM/YYYY', true).isValid()
      ? moment.utc(patient.dob, 'DD/MM/YYYY')
      : moment.utc(patient.dob, 'YYYY-MM-DD HH:mm:ss');
    patient.dob = momentDob.format('DD/MM/YYYY');
  }
  if (patient.admissionDate) {
    patient.admissionDate = moment(patient.admissionDate, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY');
  }
  if (patient.medicalDateJoined) {
    patient.medicalDateJoined = moment(patient.medicalDateJoined, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY');
  }
  return patient;
}

function isAlert(patient: any): boolean {
  return patient.requiresValidation === true || patient.requiresICDValidation === true || patient.wasSelfBoarded === true;
}

/**
 * Splits the loaded patients into the patient list tabs. `currentAlerts` keeps alerts that arrived
 * over the socket for patients not in `patients`; a row in `patients` replaces its older alert.
 */
export function splitPatientLists(patients: any[], currentAlerts: any[]): PatientLists {
  const outpatients = patients.filter(patient => patient.status === 'opd' && patient.active === true && !patient.isDeceased);

  const alertsById = new Map(currentAlerts.map(alert => [alert.id, alert]));
  patients.filter(isAlert).forEach(patient => alertsById.set(patient.id, patient));
  const loadedIds = new Set(patients.map(patient => patient.id));
  const alerts = Array.from(alertsById.values()).filter(alert =>
    loadedIds.has(alert.id) || (alert.requiresValidation || alert.requiresICDValidation || alert.wasSelfBoarded)
  );

  return {
    recentPatients: outpatients.slice(0, 25),
    inpatients: patients.filter(patient => patient.status === 'inpatient' && patient.active === true && !patient.isDeceased),
    outpatients,
    invites: patients.filter(patient =>
      (patient.status === 'invite' || patient.requiresValidation === true) && patient.active === true && !patient.isDeceased
    ),
    // Archive wins over deceased: archived patients (active === false) stay in archives only.
    archives: patients.filter(patient => patient.active === false),
    deceased: patients.filter(patient => patient.active === true && patient.isDeceased === true),
    alerts,
  };
}
